/**
 * src/v2/services/authorizationModel.ts
 *
 * The rules of authorization, with no I/O in them. Everything here is a
 * pure function of a response the backend already produced, which is what
 * makes it the layer worth testing.
 *
 * ## Why permissions are named, not inferred
 *
 * Before this module, a panel decided whether to enable a control by
 * comparing the connected wallet to a field in GeoLink metadata. That
 * answers "did I create this", which stopped being the same question as
 * "may I manage this" the moment an object could be reassigned to an
 * organization. Every helper here takes an `ObjectAuthorization` and reads
 * a permission off it; none of them compares an address to anything.
 *
 * ## Administrative and operational authority are separate axes
 *
 * A country admin may change what an object *is* — its binding, its
 * status, who operates it — without being able to operate it. The contract
 * draws that line itself: `can_manage_object` resolves through the owning
 * organization and excludes country admins, while the guards on the
 * operator and beneficiary writes admit country scope first. So
 * `canManageObject: false` alongside `canAssignOperators: true` is a
 * correct and expected combination, not a bug to normalise away.
 *
 * Anything that collapses the two either locks admins out of intervening
 * or hands them day-to-day control of every object in their country.
 */

// ── Permissions ──────────────────────────────────────────────────────────────

/**
 * Every permission the authorization service can answer.
 *
 * Deliberately flat and explicit: no permission implies another. Holding
 * MANAGE_OBJECT does not carry MANAGE_FUNDS, and being able to suspend an
 * object does not carry the ability to operate it.
 */
export type Permission =
  | 'canManageObject'
  | 'canManageRules'
  | 'canManageFunds'
  | 'canManageBinding'
  | 'canReassignObject'
  | 'canSuspendObject'
  | 'canAssignOperators'
  | 'canAssignBeneficiaries'
  | 'canCreateChildObject'
  | 'canViewFinancials';

export const PERMISSIONS: Permission[] = [
  'canManageObject',
  'canManageRules',
  'canManageFunds',
  'canManageBinding',
  'canReassignObject',
  'canSuspendObject',
  'canAssignOperators',
  'canAssignBeneficiaries',
  'canCreateChildObject',
  'canViewFinancials',
];

export const PERMISSION_LABEL: Record<Permission, string> = {
  canManageObject:        'Manage object',
  canManageRules:         'Manage rules',
  canManageFunds:         'Manage funds',
  canManageBinding:       'Manage binding',
  canReassignObject:      'Reassign',
  canSuspendObject:       'Suspend',
  canAssignOperators:     'Assign operators',
  canAssignBeneficiaries: 'Assign beneficiaries',
  canCreateChildObject:   'Create child object',
  canViewFinancials:      'View financials',
};

/**
 * Which axis a permission sits on.
 *
 * `operational` is "may I run this thing", resolved through the owning
 * organization. `administrative` is "may I intervene in what this thing
 * is", resolved through country scope. `mixed` is reachable either way.
 */
export type PermissionKind = 'operational' | 'administrative' | 'mixed';

export const PERMISSION_KIND: Record<Permission, PermissionKind> = {
  canManageObject:        'operational',
  canManageRules:         'operational',
  canManageFunds:         'operational',
  canManageBinding:       'administrative',
  canReassignObject:      'administrative',
  canSuspendObject:       'administrative',
  canCreateChildObject:   'administrative',
  canAssignOperators:     'mixed',
  canAssignBeneficiaries: 'mixed',
  canViewFinancials:      'administrative',
};

// ── Authority sources ────────────────────────────────────────────────────────

/**
 * Where an answer came from. A list rather than a winner: a wallet can be
 * both the country admin and an organization operator, and flattening that
 * to one label loses the reason a particular permission is true.
 *
 * `creator_legacy` is the transitional one. It means the decision fell
 * back to stored provenance because nothing on chain could answer, and it
 * exists so that fallback is visible in the UI instead of silent.
 */
export type AuthoritySource =
  | 'protocol_admin'
  | 'country_admin'
  | 'organization_admin'
  | 'organization_operator'
  | 'object_operator'
  | 'beneficiary'
  | 'creator_legacy';

export const SOURCE_LABEL: Record<AuthoritySource, string> = {
  protocol_admin:        'Protocol admin',
  country_admin:         'Country admin',
  organization_admin:    'Organization admin',
  organization_operator: 'Organization operator',
  object_operator:       'Object operator',
  beneficiary:           'Beneficiary',
  creator_legacy:        'Creator (legacy)',
};

export const SOURCE_EXPLANATION: Record<AuthoritySource, string> = {
  protocol_admin:
    'The protocol administrator. May intervene anywhere, but does not thereby '
    + 'operate any particular object.',
  country_admin:
    'The administrator for this object\'s country. May approve, reassign and '
    + 'suspend, without becoming its operator.',
  organization_admin:
    'An administrator of the owning organization. Runs the object and may '
    + 'delegate it.',
  organization_operator:
    'An operator of the owning organization. Runs the object and authors its '
    + 'rules, but cannot redirect its payouts.',
  object_operator:
    'Holds a delegated grant on this specific object. Limited to the bits in '
    + 'that grant.',
  beneficiary:
    'Approved to receive value from the owning organization. Carries no '
    + 'management authority at all.',
  creator_legacy:
    'Recorded as the creator in GeoLink. Used only because nothing on chain '
    + 'could answer, and it will stop being honoured once the object is '
    + 'registered and bound.',
};

// ── Response shapes (mirror server/v2/routes/authorization.js) ────────────────

export interface ListAdvisory {
  configured: boolean;
  pkHash: string | null;
  status: number | null;
  statusName: string | null;
  denied: boolean;
  watched: boolean;
  /** Always false: no contract consults this list on an authorization path. */
  enforcedOnChain: boolean;
}

export interface DegradedRead {
  contract: string;
  call: string;
  error: string;
}

export interface OrganizationSummary {
  numericCode: number | null;
  alpha2: string | null;
  status: number;
  statusName: string;
  verificationV2: {
    identityVerified: boolean;
    authorityVerified: boolean;
    beneficiaryVerified: boolean;
  } | null;
  capabilities: {
    canOwnObjects: boolean;
    canOperateObjects: boolean;
    canReceiveFunds: boolean;
  };
}

export interface ObjectAuthorizationContext {
  numericCode: number | null;
  alpha2: string | null;
  status: number;
  statusName: string;
  parentId: string | null;
  parentUuid: string | null;
  binding: {
    organizationId: string | null;
    relationship: number;
    relationshipName: string | null;
    status: number;
    statusName: string;
  } | null;
  effectiveOrganizationId: string | null;
  inherited: boolean;
  organization: OrganizationSummary | null;
  beneficiary: string | null;
  canObjectReceive: boolean;
  pendingTransfer: { fromOrg: string | null; toOrg: string | null } | null;
  grant: {
    mask: number;
    manageObject: boolean;
    manageRules: boolean;
    manageFunds: boolean;
  };
}

export type ObjectAuthorization = {
  objectId: string;
  objectUuid: string;
  wallet: string;
  registered: boolean;
  authoritySources: AuthoritySource[];
  reasons: Partial<Record<Permission, string>>;
  advisory: ListAdvisory;
  context: ObjectAuthorizationContext;
  degraded: DegradedRead[];
} & Record<Permission, boolean>;

export interface CountryAuthorization {
  wallet: string;
  numericCode: number;
  alpha2: string | null;
  isProtocolAdmin: boolean;
  isCountryAdmin: boolean;
  canRegisterObjects: boolean;
  canApproveBindings: boolean;
  canReassignObjects: boolean;
  canSuspendObjects: boolean;
  canAdministerOrganizations: boolean;
  authoritySources: AuthoritySource[];
  advisory: ListAdvisory;
  /** False means we could not ask, which is not the same as a refusal. */
  resolved: boolean;
  degraded: DegradedRead[];
}

export interface OrganizationAuthorization {
  orgId: string;
  wallet: string;
  numericCode: number | null;
  alpha2: string | null;
  status: number;
  statusName: string;
  verificationV2: OrganizationSummary['verificationV2'];
  capabilities: OrganizationSummary['capabilities'];
  canAdministerOrganization: boolean;
  canGrantRoles: boolean;
  canVerify: boolean;
  canSetStatus: boolean;
  isOrganizationAdmin: boolean;
  isOrganizationOperator: boolean;
  isApprovedBeneficiary: boolean;
  authoritySources: AuthoritySource[];
  advisory: ListAdvisory;
  degraded: DegradedRead[];
}

// ── Reading a permission ─────────────────────────────────────────────────────

/**
 * Whether a permission is granted.
 *
 * Absent authorization is a refusal, not a pass. Every caller that used to
 * write `creator === wallet || !creator` should come through here instead,
 * because "we do not know" and "yes" were the same branch in that code and
 * that is the bug.
 */
export function can(
  auth: ObjectAuthorization | null | undefined,
  permission: Permission,
): boolean {
  return !!auth && auth[permission] === true;
}

/** Whether every listed permission is granted. */
export function canAll(
  auth: ObjectAuthorization | null | undefined,
  permissions: Permission[],
): boolean {
  return permissions.every((p) => can(auth, p));
}

/** Whether at least one listed permission is granted. */
export function canAny(
  auth: ObjectAuthorization | null | undefined,
  permissions: Permission[],
): boolean {
  return permissions.some((p) => can(auth, p));
}

/**
 * Why a permission is not granted, in a sentence fit for a tooltip.
 *
 * Prefers the backend's own reason, because it was produced with the chain
 * state in hand. Falls back to explaining the absence itself, which is the
 * case a UI is most likely to render while a request is still in flight.
 */
export function explain(
  auth: ObjectAuthorization | null | undefined,
  permission: Permission,
): string {
  if (!auth) return 'Authorization has not been resolved for this object yet.';
  if (auth[permission] === true) return '';

  const given = auth.reasons?.[permission];
  if (given) return given;

  if (isUnknown(auth, permission)) {
    return 'This could not be checked against the chain, so the action is '
      + 'unavailable rather than assumed allowed.';
  }
  return `${PERMISSION_LABEL[permission]} is not available to this wallet.`;
}

/**
 * Whether a false permission is a refusal or an unanswered question.
 *
 * These are different facts and a debugging panel has to tell them apart:
 * a refusal is the system working, an unanswered question is a contract or
 * an RPC that could not be reached. Both deny, which is correct, but only
 * one of them is a problem to go and fix.
 */
export function isUnknown(
  auth: ObjectAuthorization | null | undefined,
  permission: Permission,
): boolean {
  if (!auth) return true;
  if (auth[permission] === true) return false;
  return auth.degraded.length > 0 && !auth.reasons?.[permission];
}

/** True when any part of the answer could not be read from the chain. */
export function isDegraded(auth: { degraded?: DegradedRead[] } | null | undefined): boolean {
  return !!auth?.degraded?.length;
}

/**
 * One sentence naming what could not be read, for a warning banner.
 *
 * Worth surfacing rather than swallowing: the usual cause is a contract
 * that has not been upgraded to export a function the service asks for,
 * and the visible symptom is a control that is disabled for no apparent
 * reason.
 */
export function describeDegradation(
  auth: { degraded?: DegradedRead[] } | null | undefined,
): string | null {
  const reads = auth?.degraded ?? [];
  if (!reads.length) return null;

  const calls = Array.from(new Set(reads.map((d) => `${d.contract}.${d.call}`)));
  const list = calls.slice(0, 3).join(', ');
  const more = calls.length > 3 ? ` and ${calls.length - 3} more` : '';
  return `Part of this answer could not be read from the chain (${list}${more}). `
    + 'Affected permissions are shown as unavailable rather than guessed, so some '
    + 'actions may be hidden that would otherwise be allowed.';
}

// ── Sources ──────────────────────────────────────────────────────────────────

export function hasSource(
  auth: { authoritySources?: AuthoritySource[] } | null | undefined,
  source: AuthoritySource,
): boolean {
  return !!auth?.authoritySources?.includes(source);
}

/** True when the only thing standing behind an answer is stored provenance. */
export function isLegacyOnly(
  auth: { authoritySources?: AuthoritySource[] } | null | undefined,
): boolean {
  const sources = auth?.authoritySources ?? [];
  return sources.length === 1 && sources[0] === 'creator_legacy';
}

export function sourceLabels(
  auth: { authoritySources?: AuthoritySource[] } | null | undefined,
): string[] {
  return (auth?.authoritySources ?? []).map((s) => SOURCE_LABEL[s] ?? s);
}

/**
 * A short phrase describing the wallet's standing, for a header line.
 *
 * Lists every source rather than picking the strongest, because "country
 * admin and organization operator" explains a permission set that neither
 * label explains on its own.
 */
export function describeAuthority(
  auth: { authoritySources?: AuthoritySource[] } | null | undefined,
): string {
  const labels = sourceLabels(auth);
  if (!labels.length) return 'No recorded authority';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

// ── Grouping for display ─────────────────────────────────────────────────────

export interface PermissionRow {
  permission: Permission;
  label: string;
  kind: PermissionKind;
  allowed: boolean;
  /** False and unexplained: the question could not be asked. */
  unknown: boolean;
  reason: string;
}

/**
 * The whole permission set as rows, ready to render.
 *
 * Ordered operational first, then administrative, then mixed, so the
 * panel reads as "what can this wallet do with the object" before "what
 * can it do to the object".
 */
export function permissionRows(
  auth: ObjectAuthorization | null | undefined,
): PermissionRow[] {
  const order: PermissionKind[] = ['operational', 'administrative', 'mixed'];
  return [...PERMISSIONS]
    .sort((a, b) => {
      const d = order.indexOf(PERMISSION_KIND[a]) - order.indexOf(PERMISSION_KIND[b]);
      return d !== 0 ? d : PERMISSIONS.indexOf(a) - PERMISSIONS.indexOf(b);
    })
    .map((permission) => ({
      permission,
      label: PERMISSION_LABEL[permission],
      kind: PERMISSION_KIND[permission],
      allowed: can(auth, permission),
      unknown: isUnknown(auth, permission),
      reason: explain(auth, permission),
    }));
}

// ── Compliance advisory ──────────────────────────────────────────────────────

/**
 * A warning about the wallet's compliance-list standing, or null.
 *
 * Explicitly worded as advice. `geo-compliance-list` is keyed by a hash of
 * the public key and a contract holds an `Address`, so no contract can
 * reproduce the key to look a wallet up — the spatial registry's
 * `require_not_denied` is inert by design. That means this check exists
 * only in the UI, and a UI-only check must not be described as if the
 * chain would refuse the transaction.
 */
export function describeAdvisory(advisory: ListAdvisory | null | undefined): string | null {
  if (!advisory?.configured) return null;
  if (advisory.denied) {
    return 'This wallet is on the denial list. The contracts do not currently '
      + 'enforce that list, so this is a warning and not a block: the write '
      + 'would still succeed on chain.';
  }
  if (advisory.watched) {
    return 'This wallet is on the watch list. Actions are not restricted; the '
      + 'flag is recorded for review.';
  }
  return null;
}

// ── Legacy provenance, during migration only ─────────────────────────────────

/**
 * Whether stored provenance may decide, for a thing the chain cannot yet
 * answer about.
 *
 * Two rules, both learned from the code this replaces. An object the chain
 * *does* know about is never decided by provenance, so a reassignment
 * cannot be overridden by whoever happened to create the row. And an
 * absent creator denies rather than allows — the previous implementation
 * returned true when no creator was recorded, which made every legacy
 * object editable by any connected wallet.
 */
export function legacyCreatorMayAct(
  creatorWallet: string | null | undefined,
  wallet: string | null | undefined,
  chainKnowsObject: boolean,
): boolean {
  if (chainKnowsObject) return false;
  const creator = (creatorWallet ?? '').trim();
  const actor = (wallet ?? '').trim();
  if (!creator || !actor) return false;
  return creator === actor;
}

/** The sentence to show when an action rests on provenance alone. */
export const LEGACY_NOTICE =
  'This object is not in the spatial registry, so authority falls back to the '
  + 'wallet recorded as its creator. Register and bind it to decide this on '
  + 'chain instead.';
